import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import i18n from '@/core/i18n';
import { formatHoursToTimeString } from '@/helpers';
import { getMemberStatusText } from '@/modules/common';
import { mapMemberStatusFromDTO } from '@/modules/common/mappers/member-status.mapper';
import { MemberStatusDTO } from '@/modules/common/types/dto/member-status.dto';
import { TeamMemberChangedFieldName } from '@/modules/common/types/dto/response/team-member-changed-field-response';
import { MembershipType } from '@/modules/team/types/membership-type';

import { calculateRightCenterPosition } from '../../helpers/tooltip-helpers';
import { PlannerUserStateChangeResponse } from '../../types/dto';
import { TooltipVariant, usePlannerTooltip } from '../tooltip/planner-tooltip-provider';

interface StateChangeIndicatorProps {
  /** The state change containing the changed fields */
  stateChange: PlannerUserStateChangeResponse;
  /** Pixel position from the left edge of the timeline */
  leftPosition: number;
  /** Height of the timeline row */
  height: number;
}

/**
 * Formats a field name into a human-readable label using translations
 */
const getFieldLabel = (fieldName: TeamMemberChangedFieldName): string => {
  const fieldLabels: Record<TeamMemberChangedFieldName, string> = {
    [TeamMemberChangedFieldName.STATUS]: i18n.t('authenticated.planner:stateChanges.fields.status'),
    [TeamMemberChangedFieldName.DEPARTMENT]: i18n.t(
      'authenticated.planner:stateChanges.fields.department'
    ),
    [TeamMemberChangedFieldName.ROLE]: i18n.t('authenticated.planner:stateChanges.fields.role'),
    [TeamMemberChangedFieldName.SENIORITY]: i18n.t(
      'authenticated.planner:stateChanges.fields.seniority'
    ),
    [TeamMemberChangedFieldName.LOCATION]: i18n.t(
      'authenticated.planner:stateChanges.fields.location'
    ),
    [TeamMemberChangedFieldName.MANAGER]: i18n.t(
      'authenticated.planner:stateChanges.fields.manager'
    ),
    [TeamMemberChangedFieldName.IS_MANAGER]: i18n.t(
      'authenticated.planner:stateChanges.fields.isManager'
    ),
    [TeamMemberChangedFieldName.CAPACITY]: i18n.t(
      'authenticated.planner:stateChanges.fields.capacity'
    ),
    [TeamMemberChangedFieldName.ON_DEMAND]: i18n.t(
      'authenticated.planner:stateChanges.fields.onDemand'
    ),
    [TeamMemberChangedFieldName.INTERNAL_RATE]: i18n.t(
      'authenticated.planner:stateChanges.fields.internalRate'
    ),
    [TeamMemberChangedFieldName.EXTERNAL_RATE]: i18n.t(
      'authenticated.planner:stateChanges.fields.externalRate'
    ),
    [TeamMemberChangedFieldName.IS_HOURLY_RATED]: i18n.t(
      'authenticated.planner:stateChanges.fields.isHourlyRated'
    ),
    [TeamMemberChangedFieldName.CURRENCY]: i18n.t(
      'authenticated.planner:stateChanges.fields.currency'
    ),
    [TeamMemberChangedFieldName.TOTAL_WORKING_MINUTES]: i18n.t(
      'authenticated.planner:stateChanges.fields.workingMinutesPerDay'
    ),
    [TeamMemberChangedFieldName.MEMBERSHIP_TYPE]: i18n.t(
      'authenticated.planner:stateChanges.fields.membershipType'
    ),
  };

  return fieldLabels[fieldName] || fieldName;
};

/**
 * Formats a field value for display in the tooltip
 */
const formatFieldValue = (
  value: object | null | undefined | string,
  fieldName: TeamMemberChangedFieldName
): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  // if value is capacity in minutes format it to "2h 30m" like string
  if (fieldName === TeamMemberChangedFieldName.TOTAL_WORKING_MINUTES && typeof value === 'string') {
    const number = Number(value);

    return Number.isNaN(number) ? value : formatHoursToTimeString(number / 60);
  }

  // Billable capacity percent handling
  if (fieldName === TeamMemberChangedFieldName.CAPACITY && typeof value === 'string') {
    const number = Number(value);

    return Number.isNaN(number) ? value : i18n.t('common:percent', { value: Math.round(number) });
  }

  // Status handling
  if (fieldName === TeamMemberChangedFieldName.STATUS && typeof value === 'string') {
    const status = mapMemberStatusFromDTO(value as MemberStatusDTO);

    const statusText = getMemberStatusText(status);

    return statusText;
  }

  // Membership type — localize the raw DTO enum (e.g. "IN_HOUSE" → "In-House").
  if (fieldName === TeamMemberChangedFieldName.MEMBERSHIP_TYPE && typeof value === 'string') {
    const membershipTypeKey: Record<MembershipType, string> = {
      [MembershipType.InHouse]: 'authenticated.planner:filters.membershipTypes.inHouse',
      [MembershipType.Partner]: 'authenticated.planner:filters.membershipTypes.partner',
      [MembershipType.Contractor]: 'authenticated.planner:filters.membershipTypes.contractor',
    };
    const key = membershipTypeKey[value as MembershipType];
    return key ? i18n.t(key) : value;
  }

  // If value is an object with a 'name' property (like Department, Role, etc.)
  if (typeof value === 'object' && 'name' in value && typeof value.name === 'string') {
    return value.name;
  }

  // If value is a boolean
  if (typeof value === 'string' && (value === 'true' || value === 'false')) {
    return value === 'true' ? i18n.t('common:yes') : i18n.t('common:no');
  }

  // If value is a number or string
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  // Fallback: stringify the object
  return JSON.stringify(value);
};

/**
 * Vertical indicator line that appears at the left edge of a timeline cell
 * when a user's state changes (department, role, capacity, etc.)
 */
export const StateChangeIndicator = ({
  stateChange,
  leftPosition,
  height,
}: StateChangeIndicatorProps) => {
  const { t } = useTranslation('authenticated.planner');
  const { showTooltip, hideTooltip } = usePlannerTooltip();

  // Use the explicit field from the API response instead of inferring from empty changedFields
  const isInitialChange = stateChange.isInitialChange;
  const hasChangedFields = stateChange.changedFields.length > 0;

  const totalHours = stateChange.workingMinutesPerDay / 60;
  const formattedTotalHours = formatHoursToTimeString(totalHours);
  const billablePercent = Math.round(stateChange.billableCapacityPercent ?? 100);
  const nonBillablePercent = 100 - billablePercent;
  const billableHours = totalHours * ((stateChange.billableCapacityPercent ?? 100) / 100);
  const nonBillableHours = totalHours - billableHours;
  const formattedBillableHours = formatHoursToTimeString(billableHours);
  const formattedNonBillableHours = formatHoursToTimeString(nonBillableHours);

  const tooltipContent = useMemo(
    () => (
      <div className="space-y-1 px-3 py-1.5 text-xs">
        {/* Changed fields enumeration */}
        {hasChangedFields && (
          <>
            <span className="block border-b border-primary-foreground/30 pb-1 font-medium text-primary-foreground">
              {t('stateChanges.teamMemberInfoChanged')}
            </span>
            <ul className="list-disc space-y-0.5 pl-4 pt-1 text-primary-foreground/70">
              {stateChange.changedFields.map((change, index) => {
                if (change.oldValue === null && change.newValue === null) return null;

                const label = getFieldLabel(change.fieldName);
                const oldValue = formatFieldValue(change.oldValue, change.fieldName);
                const newValue = formatFieldValue(change.newValue, change.fieldName);

                return (
                  <li key={`${change.fieldName}-${index}`}>
                    <span className="font-medium text-primary-foreground">{label}:</span>{' '}
                    <span className="line-through">{oldValue}</span>
                    {` → `}
                    <span className="font-medium">{newValue}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {/* Team member initial info */}
        {isInitialChange && (
          <>
            <span className="block border-b border-primary-foreground/30 pb-1 font-medium text-primary-foreground">
              {t('stateChanges.teamMemberAdded')}
            </span>
            <ul className="list-disc space-y-0.5 pl-4 pt-1 text-primary-foreground/70">
              {stateChange.department && (
                <li>
                  <span className="font-medium text-primary-foreground">
                    {t('stateChanges.fields.department')}:
                  </span>{' '}
                  <span className="font-medium">{stateChange.department.name}</span>
                </li>
              )}
              {stateChange.role && (
                <li>
                  <span className="font-medium text-primary-foreground">
                    {t('stateChanges.fields.role')}:
                  </span>{' '}
                  <span className="font-medium">{stateChange.role.name}</span>
                </li>
              )}
              {stateChange.seniority && (
                <li>
                  <span className="font-medium text-primary-foreground">
                    {t('stateChanges.fields.seniority')}:
                  </span>{' '}
                  <span className="font-medium">{stateChange.seniority.name}</span>
                </li>
              )}
              {typeof stateChange.workingMinutesPerDay === 'number' && (
                <>
                  <li>
                    <span className="font-medium text-primary-foreground">
                      {t('stateChanges.totalCapacity')}:
                    </span>{' '}
                    <span className="font-medium">
                      {stateChange.onDemand
                        ? t('stateChanges.totalCapacityOnDemand')
                        : formattedTotalHours}
                    </span>
                  </li>
                  {!stateChange.onDemand && (
                    <>
                      <li>
                        <span className="font-medium text-primary-foreground">
                          {t('stateChanges.billableCapacity')}:
                        </span>{' '}
                        <span className="font-medium">
                          {t('stateChanges.capacityWithHours', {
                            percent: t('common:percent', { value: billablePercent }),
                            hours: formattedBillableHours,
                          })}
                        </span>
                      </li>
                      <li>
                        <span className="font-medium text-primary-foreground">
                          {t('stateChanges.nonBillableCapacity')}:
                        </span>{' '}
                        <span className="font-medium">
                          {t('stateChanges.capacityWithHours', {
                            percent: t('common:percent', { value: nonBillablePercent }),
                            hours: formattedNonBillableHours,
                          })}
                        </span>
                      </li>
                    </>
                  )}
                </>
              )}
            </ul>
          </>
        )}
      </div>
    ),
    [
      hasChangedFields,
      isInitialChange,
      stateChange,
      formattedTotalHours,
      billablePercent,
      nonBillablePercent,
      formattedBillableHours,
      formattedNonBillableHours,
      t,
    ]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { rightX, centerY } = calculateRightCenterPosition(e.currentTarget);
      showTooltip(tooltipContent, rightX, centerY, TooltipVariant.StateChange);
    },
    [tooltipContent, showTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  return (
    <button
      type="button"
      className="group absolute top-0 flex h-full cursor-help items-center justify-center before:absolute before:inset-y-0 before:left-[-24px] before:right-[-24px] before:content-['']"
      style={{
        left: `${leftPosition}px`,
        height: `${height}px`,
      }}
      aria-label={t('stateChanges.indicatorLabel')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Dashed vertical line with hover animation */}
      <div className="relative h-full" style={{ marginLeft: '-2px' }}>
        <div className="absolute inset-0 border-l-2 border-dashed border-zinc-400 transition-all duration-300 group-hover:opacity-0 dark:border-zinc-500" />
        <div className="absolute inset-0 border-l-2 border-dashed border-accent-foreground opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:shadow-[0_0_8px_rgba(0,0,0,0.3)]" />
      </div>

      {/* Triangle at the top - pointing down */}
      <div
        className="absolute text-zinc-400 transition-all duration-300 group-hover:scale-110 group-hover:text-accent-foreground dark:text-zinc-500"
        style={{
          top: '-2px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid currentColor',
        }}
      />

      {/* Triangle at the bottom - pointing up */}
      <div
        className="absolute text-zinc-400 transition-all duration-300 group-hover:scale-110 group-hover:text-accent-foreground dark:text-zinc-500"
        style={{
          bottom: '-2px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '8px solid currentColor',
        }}
      />
    </button>
  );
};
